import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';

const ToastContext = createContext(null);

let toastIdCounter = 0;

const TOAST_DURATION = 7000;

const TOAST_TYPES = {
  success: { className: 'is-success', icon: 'bi-check-circle-fill' },
  error: { className: 'is-danger', icon: 'bi-exclamation-triangle-fill' },
  warning: { className: 'is-warning', icon: 'bi-exclamation-circle-fill' },
  info: { className: 'is-info', icon: 'bi-info-circle-fill' },
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef({});

  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    if (timersRef.current[id]) {
      clearTimeout(timersRef.current[id]);
      delete timersRef.current[id];
    }
  }, []);

  const showToast = useCallback((message, type = 'info') => {
    const id = ++toastIdCounter;
    setToasts((prev) => [...prev, { id, message, type }]);
    timersRef.current[id] = setTimeout(() => {
      dismissToast(id);
    }, TOAST_DURATION);
  }, [dismissToast]);

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      Object.values(timers).forEach(clearTimeout);
    };
  }, []);

  return (
    <ToastContext.Provider value={showToast}>
      {children}
      <div className="toast-container" aria-live="polite">
        {toasts.map((toast) => (
          <Toast key={toast.id} toast={toast} onDismiss={dismissToast} />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

const Toast = ({ toast, onDismiss }) => {
  const { className, icon } = TOAST_TYPES[toast.type] || TOAST_TYPES.info;
  return (
    <div className={`toast notification ${className}`} role="alert">
      <button className="delete" onClick={() => onDismiss(toast.id)} aria-label="close"></button>
      <div className="toast-content">
        <span className="toast-icon">
          <i className={`bi ${icon}`}></i>
        </span>
        <span className="toast-message">{toast.message}</span>
      </div>
    </div>
  );
};
